import { observable, autorun, makeObservable } from 'mobx'
import * as R from 'ramda'
import * as moment from 'moment-timezone'
import i18n from 'i18next'

import User from './User'

class UserStore {
    @observable filteredList = []

    active = true

    inActive = false

    @observable user = Object.seal(new User({}))

    @observable userAccess = observable.map({})

    @observable userAccessLabel = ''

    @observable formFields = []

    salutations = []

    roles = []

    timeZones = []

    @observable accessItems = []

    oid = 0

    @observable accessItemFields = {}

    passwordResetLink = null

    @observable formLoading = true

    constructor(store) {
        makeObservable(this)
        this.appStore = store
        autorun(() => {
            if (this.formFields.length > 0) this.formLoading = false
        })
    }

    initSearch = async () => {
        try {
            this.users = await this.getUsers()
            this.search()
        } catch (error) {
            console.log('UserStore initSearch error ', error)
        }
    }

    initUser = async (userId, handleCloseModal) => {
        const { tokenData: { clientId } } = this.appStore

        this.formLoading = true

        this.user = Object.seal(new User({}))
        this.userAccess = observable.map({})

        try {
            const [salutations, roles, timeZones] = await Promise.all([
                this.getSalutations(),
                this.getRoles(),
                this.getTimeZones(),
            ])

            this.salutations = salutations
            this.roles = roles
            this.timeZones = timeZones

            // only for user edit
            if (userId !== 0) {
                if (this.appStore.userData.role > this.user.roles) {
                    // this.appStore.addNotification(null, 'Unable to edit the user', 'error')
                    return
                }
                const userRes = await this.getUser(userId)
                this.user = userRes.user
                const userAccessMap = userRes.userAccess

                // redirect if clientId of logged-in user is different from selected user
                if (this.user.clientId !== parseInt(clientId, 10)) {
                    window.location.href = '/home/e/01011'
                    return
                }

                [this.oid, this.accessItems] = await this.changeRole(this.user.roles)

                this.formatUserAccess(this.accessItems, userAccessMap, this.oid)

                const passwordResets = await this.getActivePasswordResets(userId)
                this.passwordResetLink = !R.isEmpty(passwordResets)
                    ? `https://${window.location.hostname}/passwordreset/resetform/UUID/${passwordResets}`
                    : null
            }
            this.defineFormFields(salutations, roles, timeZones)
        } catch (error) {
            console.log('UserStore error ', error)
            handleCloseModal()
            // this.appStore.addNotification(null, 'Failed to connect with server', 'error')
        }
    }

    formatUserAccess = (accessItems, userAccessMap, oid) => {
        Object.keys(accessItems).forEach((key) => {
            accessItems[key].forEach((accItem) => {
                const userPortfoliosAccess = userAccessMap.get(oid)
                const hasAccess = Boolean(userPortfoliosAccess[accItem.id])
                this.setAccessRights({ value: hasAccess, name: accItem.id, key })
            })
        })
    }

    defineFormFields = (salutations, roles, timeZones) => {
        const { appStore } = this

        const pptAccessOptions = Object.freeze([
            { value: 0, label: i18n.t('user.form.powerpointAccess.0') },
            { value: 1, label: i18n.t('user.form.powerpointAccess.1') },
            { value: 2, label: i18n.t('user.form.powerpointAccess.2') },
        ])

        this.accessItemFields = this.getAccessItemsFields()

        this.formFields = [
            {
                type: 'select',
                name: 'salutation',
                label: i18n.t('user.form.salutation', 'Title'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.user.salutation,
                options: salutations,
                isMandatory: true,
                errorMessage: i18n.t('error_salutation'),
            },
            {
                type: 'input',
                name: 'firstName',
                label: i18n.t('user.form.firstName', 'First name'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.user.firstName,
                isMandatory: true,
                errorMessage: i18n.t('error_first_name'),
            },
            {
                type: 'input',
                name: 'lastName',
                label: i18n.t('user.form.lastName', 'Last name'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.user.lastName,
                isMandatory: true,
                errorMessage: i18n.t('error_last_name'),
            },
            {
                type: 'input',
                name: 'email',
                label: i18n.t('user.form.email', 'Email'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.user.email,
                isMandatory: true,
                errorMessage: i18n.t('error_email'),
                formatError: i18n.t('format_error_email'),
                format: (value) => (
                    value.match(/.*@.*\..*/gi)
                ),
            },
            {
                type: 'input',
                name: 'jobTitle',
                label: i18n.t('user.form.jobTitle', 'Job title'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.user.jobTitle,
                isMandatory: true,
                errorMessage: i18n.t('error_job_title'),
            },
            {
                type: 'select',
                name: 'roles',
                label: i18n.t('user.form.role', 'User role'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.user.roles,
                options: roles,
                isMandatory: true,
                errorMessage: i18n.t('error_user_role'),
                isDisabled: !this.roleIsEditable(),
            },
            {
                type: 'user-access-checkbox-accordion',
                name: 'userAccessCheckboxAccordion',
                label: this.userAccessLabel,
                fields: this.accessItemFields,
                isHide: !(Object.keys(this.accessItemFields).length > 0),

            },
            {
                type: 'select',
                name: 'timeZone',
                label: i18n.t('user.form.timezone', 'Timezone'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.user.timeZone,
                options: timeZones,
                isMandatory: true,
                errorMessage: i18n.t('error_user_timezone'),
            },
            {
                type: 'checkbox-reactive',
                name: 'editUserFeatures',
                label: 'Edit user features',
                fields: [
                    {
                        type: 'checkbox',
                        label: i18n.t(process.env.USE_BENCHMARKING_MODAL
                            ? 'user.form.benchmark.explori'
                            : 'user.form.benchmark.industry'),
                        name: 'industryBenchmarkPrivileges',
                        defaultChecked: this.user.industryBenchmarkPrivileges,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.benchmark.company'),
                        name: 'companyBenchmarkPrivileges',
                        defaultChecked: this.user.companyBenchmarkPrivileges,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.benchmark.division'),
                        name: 'divisionBenchmarkPrivileges',
                        defaultChecked: this.user.divisionBenchmarkPrivileges,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.benchmark.portfolio'),
                        name: 'portfolioBenchmarkPrivileges',
                        defaultChecked: this.user.portfolioBenchmarkPrivileges,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.anonymizeReport'),
                        name: 'anonymizeReport',
                        defaultChecked: this.user.anonymizeReport,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.hideDistribute'),
                        name: 'hideDistribute',
                        defaultChecked: this.user.hideDistribute,
                        isHide: !appStore.isMasterSuper(),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.deliveryReport'),
                        name: 'isDeliveryReportEnabled',
                        defaultChecked: this.user.isDeliveryReportEnabled,
                        isHide: !(appStore.isMasterSuper() && appStore.isExploriAdmin()),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.isExploriReseller'),
                        name: 'isExploriReseller',
                        defaultChecked: this.user.isExploriReseller,
                        isHide: !(appStore.isMasterSuper() && appStore.isExploriAdmin()),
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('user.form.useSSO'),
                        name: 'useSSO',
                        defaultChecked: this.user.useSSO,
                        isHide: !(appStore.isMasterSuper() && appStore.isExploriAdmin()),
                    },
                ],
            },
            {
                type: 'select',
                name: 'allowPPT',
                label: i18n.t('user.form.powerpointAccess'),
                defaultValue: this.user.allowPPT,
                options: pptAccessOptions,
                isHide: !(appStore.isMasterSuper() && appStore.isExploriAdmin()),
            },
            {
                type: 'checkbox',
                label: i18n.t('user.form.sendPassword'),
                name: 'sendLoginDetails',
                defaultChecked: this.user.sendLoginDetails,
            },
        ]
    }

    setField = async ({ value, name }) => {
        console.log(`setField ${value} => ${name}`)

        if (name.startsWith('selectAll')) {
            const userAccessKey = name.split('-')[1]
            this.accessItemFields[userAccessKey].options.forEach((option) => {
                this.setField({ value, name: option.name })
            })
        } else if (name.startsWith('userAccess')) {
            const accessKey = name.split('_')[1]
            const accessId = name.split('_')[2]
            this.setAccessRights({ value, name: accessId, key: accessKey })
            this.defineFormFields(this.salutations, this.roles, this.timeZones)
        } else this.user[name] = value
        if (name === 'roles') {
            [this.oid, this.accessItems] = await this.changeRole(this.user.roles)
            this.userAccess = observable.map({})
            this.defineFormFields(this.salutations, this.roles, this.timeZones)
        }
    }

    resetFormState = () => {
        this.formFields = []
        this.accessItems = []
    }

    toggleActive = () => {
        this.active = !this.active
        this.search()
    }

    toggleInActive = () => {
        this.inActive = !this.inActive
        this.search()
    }

    resetFilter = () => {
        this.active = true
        this.inActive = false
        this.search()
    }

    setAccessRights = ({ value, name, key }) => {
        const { userAccess } = this
        if (userAccess.has(key)) {
            const keyMap = userAccess.get(key)
            if (value) {
                keyMap.set(`${this.oid}_${name}`, true)
            } else {
                keyMap.delete(`${this.oid}_${name}`)
            }
            userAccess.set(key, keyMap)
        } else if (value) userAccess.set(key, new Map().set(`${this.oid}_${name}`, true))
    }

    getAccessItemsFields = () => {
        const {
            appStore: { tokenData }, user, userAccess, oid, accessItems,
        } = this
        const accessItemFields = {}

        const sameUser = (user.userId === tokenData.userId)
        const items = sameUser
            ? this.getActiveAccessItems(accessItems, oid)
            : accessItems

        Object.keys(items).forEach((key) => {
            accessItemFields[key] = {
                type: 'option-list',
                name: 'selectAllCheck',
                selectAll: {
                    type: 'checkbox',
                    labelDir: 'left',
                    cssClass: 'select-all-check',
                    label: i18n.t('user.form.selectAll', { 1: Array.from(userAccess.get(key) || []).length,2: items[key].length }),
                    name: `selectAll-${key}`,
                    checked: Array.from(userAccess.get(key) || []).length === items[key].length,
                },
                options: items[key].map(({ id, name }) => (
                    {
                        type: 'checkbox',
                        label: name.replace(/&amp;/gi, '&'),
                        labelDir: 'left',
                        name: `userAccess_${key}_${id}`,
                        checked: userAccess.get(key)?.get(`${oid}_${id}`),
                        disabled: user.userId === tokenData.userId,
                    }
                )),
            }
        })
        return accessItemFields
    }

    getUserAccess = (role) => {
        let itemRequest
        let oid

        switch (role) {
            case 120:
                itemRequest = this.getDivisions
                oid = 4
                this.userAccessLabel = i18n.t('user.form.selectDivisions')
                break
            case 130:
                itemRequest = this.getPortfolios
                oid = 5
                this.userAccessLabel = i18n.t('user.form.selectPortfolios')
                break
            case 140: case 170:
                itemRequest = this.getProducts
                oid = 6
                this.userAccessLabel = i18n.t('user.form.selectProducts')
                break
            default:
                itemRequest = async () => []
        }

        return [oid, itemRequest]
    }

    changeRole = async (userRoles) => {
        const [oid, itemRequest] = this.getUserAccess(userRoles)

        const accessItems = await itemRequest()
        const groupedAcccessItems = R.pipe(
            R.sortWith([
                R.ascend(R.prop('parentName')),
                R.ascend(
                    R.compose(
                        R.toLower,
                        R.prop('name'),
                    ),
                ),
            ]),
            R.groupBy(R.prop('parentName')),
        )(accessItems)

        return [oid, groupedAcccessItems]
    }

    roleIsEditable = () => {
        const { appStore } = this
        const { tokenData } = appStore
        const { user } = this

        return user.userId !== tokenData.userId || appStore.isMasterSuper()
    }

    getPortfolios = async () => {
        const { appStore } = this
        const portfolios = await appStore.http.get('/portfolio')
        return portfolios.map((portfolio) => ({
            name: portfolio.name,
            id: portfolio.portfolioId,
            parentName: portfolio.divisionName,
        }))
    }

    getDivisions = async () => {
        const { appStore } = this
        const divisions = await appStore.http.get('/division')
        return divisions.map((division) => ({
            name: division.name,
            id: division.divisionId,
            parentName: R.path(['userData', 'clientName'], appStore),
        }))
    }

    getProducts = async () => {
        const { appStore } = this
        const [productRequest, portfolioRequest] = await Promise.all([
            appStore.http.get('/product'),
            this.getPortfolios(),
        ])
        // const products = await productRequest.json()
        const portfolios = portfolioRequest.reduce((map, { id, name }) => {
            map[id] = name
            return map
        }, {})
        return productRequest.map(({
            name, edition, productId, portfolioId,
        }) => ({
            name: `${name}${edition ? ` [${edition}]` : ''}`,
            id: productId,
            parentName: R.pathOr('', [portfolioId])(portfolios),
        }))
    }

    getTimeZones = async () => (
        R.sortBy(R.prop('offset'))(
            moment.tz.names()
                .filter((tz) => !tz.match(/^Etc\//gi))
                .map((tz) => {
                    const offset = moment.tz(tz).format('Z')
                    return {
                        value: tz,
                        label: `${tz}, (GMT ${offset})`,
                        offset: Number(offset.replace(':', '.')),
                    }
                }),
        )
    )

    getSalutations = async () => {
        const url = process.env.USE_NEW_LOGIN
            ? '/api/refcodes?domain=Salutation&active=1'
            : '/srv/get/refcodes/?$inlinecount=allpages&format=json&domain=Salutation&$orderby=priority&active=1'
        const salutationResponse = await fetch(url)
        const salutations = await salutationResponse.json()
        return salutations.d.results.map(({ DESCRIPTION, CODE }) => ({
            value: CODE,
            label: DESCRIPTION,
        }))
    }

    getRoles = async () => {
        const url = process.env.USE_NEW_LOGIN
            ? '/api/refcodes?domain=Role&active=1'
            : '/srv/get/refcodes/?$inlinecount=allpages&$format=json&domain=role&$orderby=priority&cols=UserEditRoles&criteria=UserEdit'
        const roleResponse = await fetch(url)
        const roles = await roleResponse.json()
        return roles.d.results.map(({ DESCRIPTION, CODE }) => ({
            value: CODE,
            label: DESCRIPTION.charAt(0).toUpperCase() + DESCRIPTION.slice(1).toLowerCase(),
        }))
    }

    getActiveAccessItems = (accesItems, userOid) => {
        const { userAccess } = this

        return Object.keys(accesItems).reduce((items, parent) => {
            const activeItems = accesItems[parent]
                .filter(({ id }) => userAccess.has(`${userOid}_${id}`))

            if (activeItems.length > 0) {
                items[parent] = activeItems
            }
            return items
        }, {})
    }

    getUser = async (userId) => {
        const userResponse = await this.appStore.http.get(`/user/${userId}`)

        const user = Object.seal(new User(userResponse.user))
        const userAccess = observable.map(
            Object.keys(userResponse.accessRights).map((k) => [
                !Number.isNaN(k) ? Number(k) : k,
                userResponse.accessRights[k].reduce((m, v) => {
                    m[v] = true
                    return m
                }, {}),
            ]),
        )
        return { user, userAccess }
    }

    getActivePasswordResets = async (userId) => (
        this.appStore.http.get(`/user/${userId}/passwordreset`)
    )

    getUsers = async () => {
        const userResponse = await this.appStore.http.get('/users')

        return userResponse.users.map((user) => ({
            active: user.active,
            email: user.email || '',
            firstName: user.firstname || '',
            lastName: user.lastname || '',
            name: '',
            role: user.role,
            userId: user.userId,
            isExploriAdmin: user.isExploriAdmin,
        }))
    }

    search = () => {
        const { users, appStore } = this
        const { searchStr } = this.searchModule

        const activeStates = [true, false]
            .filter((a) => {
                let filter = true
                if (!this.active) {
                    filter = filter && !a
                }
                if (!this.inActive) {
                    filter = filter && a
                }
                return filter
            })

        const activeFiltered = users
            .filter(({ active }) => activeStates.includes(active))
            .filter(({ isExploriAdmin }) => (
                // don't show explori users to non explori admins
                appStore.isExploriAdmin() || isExploriAdmin === false
            ))

        if (searchStr === '') {
            this.filteredList = activeFiltered
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = activeFiltered
                .filter((d) => d.userId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = activeFiltered
                .filter((d) => (
                    d.firstName.match(re)
                        || d.lastName.match(re)
                        || d.email.match(re)
                ))
        }
    }

    saveOrUpdate = async (userRequest, sendUa, handleCloseModal) => {
        const isNewUser = this.user.userId === 0
        const method = isNewUser ? 'post' : 'put'
        const endpoint = isNewUser ? '/user' : `/user/${this.user.userId}`

        try {
            await this.appStore.http[method](endpoint, JSON.stringify(userRequest))
            if (sendUa) handleCloseModal()
            const msg = this.user.userId !== 0
                ? 'User Updated'
                : 'User Created'
            await this.initSearch()
            this.appStore.addNotification(null, msg, 'success')
        } catch (e) {
            console.log(e)
            if (sendUa) handleCloseModal()
            const msg = this.user.userId !== 0
                ? 'Error updating user'
                : 'Error creating user'
            this.appStore.addNotification(null, msg, 'error')
        }
    }
}

export default UserStore
